import type { TickerCellData } from './types';

import { kind } from './types';

import {
  isCustomCellData,
  getCustomCellTemplate,
  isKindOfCustomCell,
} from '../utils';

import { isCurrencyISO } from '@/constants/currencies';
import { isCountryISO, countries } from '@/constants/countries';

const getDefaultCellData = ({
  value = null,
  currencyISO = null,
  name = null,
  uniqueSymbol = null,
  status = 'idle',
  error = null,
}: Partial<TickerCellData> = {}): TickerCellData => ({
  kind,
  value,
  currencyISO,
  name,
  uniqueSymbol,
  status,
  error,
});

const isCellData = (data: unknown): data is TickerCellData => {
  if (!isCustomCellData(data)) return false;
  return data.kind === kind;
};

/**
 * ## getCellTemplate
 *
 * @param value The value of the TickerCell
 * @param currencyISO The currencyISO to format the TickerCell value
 * @returns A custom TickerCell object and its data
 */
export const getCellTemplate = ({
  value = null,
  currencyISO,
  name,
  uniqueSymbol,
  status,
  error,
}: Omit<Partial<TickerCellData>, 'value'> & { value?: unknown } = {}) => {
  if (typeof value === 'string' || value === null) {
    const copyData = value ?? '';
    const validCurrencyISO = isCurrencyISO(currencyISO) ? currencyISO : null;
    const cellData = getDefaultCellData({
      value,
      currencyISO: validCurrencyISO,
      name,
      uniqueSymbol,
      status,
      error,
    });
    return getCustomCellTemplate({ cellData, copyData });
  }
  return getCustomCellTemplate({ cellData: getDefaultCellData() });
};

export const isCell = (
  cell: unknown
): cell is ReturnType<typeof getCellTemplate> => {
  if (typeof cell !== 'object') return false;
  if (cell === null) return false;
  if (isKindOfCustomCell(cell)) {
    return isCellData(cell.data);
  }
  return false;
};

export const getCellData = (data: unknown) => {
  if (isCellData(data) && typeof data.value === 'string') {
    return data;
  }

  if (typeof data === 'string') {
    return getDefaultCellData({ value: data });
  }

  return getDefaultCellData();
};

export const getCurrencyISO = (countryIso: string) => {
  const formattedCountryIso = countryIso.toLowerCase();
  if (isCountryISO(formattedCountryIso)) {
    const currencyISO = countries[formattedCountryIso]?.currencyISO;
    return isCurrencyISO(currencyISO) ? currencyISO : null;
  }
  return null;
};
